import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { GridColumn, Grid } from '@progress/kendo-react-grid';
import HttpStatus, { StatusCodes } from 'http-status-codes';
import { Col, Form } from 'react-bootstrap';
import { PurchaseOrderProductGetDto } from 'svila-common';
import { Formik } from 'formik';
import * as yup from 'yup';
import Modal from 'react-modal';
import { API_URL, FILE_UPLOAD_TYPES, UPLOAD_MAX_SIZE_IMAGE } from '../../../../utils/constants';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import { withState } from '../../../../components/common/grid/WrappedGrid';
import PurchaseOrdersApi from '../../../../services/api/PurchaseOrdersApi';
import PurchaseSecondaryNavigation from '../../../../components/common/navigation/PurchaseSecondaryNavigation';
import SecondaryButton from '../../../../components/common/forms/Button/SecondaryButton';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import FileInputComponent from '~/src/components/common/forms/FileInput/FileInputComponent';
import FormRow from '~/src/components/common/forms/Row/FormRow';
import FilesApi from '~/src/services/api/FilesApi';
import ImportResults from '~/src/scenes/imports/components/ResultDtoDisplay';
import PurchaseOrderProductsApi from '~/src/services/api/PurchaseOrderProductsApi';
import TableWrapper from '~/src/components/common/forms/TableWrapper/TableWrapper';
import xIcon from '../../../../static/img/xIcon.svg';
import plus from '../../../../static/img/plus.svg';
import BorderlessButton from '~/src/components/common/forms/Button/BorderlessButton';
import styled from 'styled-components';
import TextInputComponent from '~/src/components/common/forms/TextInput/TextInputComponent';
import GtinComponent from '~/src/components/common/GtinComponent';
import importIcon from '../../../../static/img/importIcon.svg';
import upload from '../../../../static/img/upload.svg';
import DisplayQuantities from '~/src/components/common/DisplayQuantities';
import GridWrapper from '~/src/components/common/GridWrapper';
import xCircle from '../../../../static/img/xCircle.svg';
import ProductService from '~/src/services/logic/ProductService';
import { validateFileStructure } from '../validationSchema';
import ImportOrderProducts from '~/src/components/common/orders/ImportOrderProductsForm';

const validationSchema = yup.object().shape({
  xlsx: yup
    .mixed()
    .test('notEmpty', 'Полето е празно', (value) => {
      if (!value || value.length === 0) {
        return false;
      }
      return true;
    })
    .test('fileSize', 'Файлът трябва да бъде под 3MB', (value) => {
      if (value && value.length > 0) {
        return value[0].size <= UPLOAD_MAX_SIZE_IMAGE;
      }
      return true;
    })
    .test(
      'fileFormat',
      'Файлът не е в правилния формат',
      async function (value) {
        if (value && value.length > 0) {
          const validationResult = await validateFileStructure(value[0]);
          if (!validationResult.valid) {
            throw this.createError({ message: validationResult.error });
          }
          return true;
        }
        return false;
      }
    )
    .required('Полето е празно'),
});

interface ProductCellProps {
  dataItem: ProductDetailsGetDto;
}

export default function PurchaseOrderProductsSelection(): JSX.Element {
  const { id } = useParams();
  const [purchaseOrderProducts, setPurchaseOrderProducts] = useState<PurchaseOrderProductGetDto[]>([]);
  const initialValues = { quantity: {} };
  const [tableValues, setTableValues] = useState(initialValues);
  const [isImport, setIsImport] = useState<boolean>(false);
  const [isAutoGenerated, setIsAutoGenerated] = useState<boolean>(false);
  const [result, setResult] = React.useState(null);
  const url = `${API_URL}/products/products/get-order-list`;
  const StatefullGrid = useMemo(() => withState(Grid, url, { pageable: { pageSizes: [50, 100, 150] } }), []);

  const getProducts = async () => {
    const result = await PurchaseOrderProductsApi.getForOrder(id);
    setPurchaseOrderProducts(result.data.orderedProducts);
    const vals = { ...tableValues };
    if (result.data) {
      result.data?.orderedProducts?.forEach((product) => {
        vals.quantity = { ...vals.quantity, [product.id]: product.originalQuantityDu };
      });
      setTableValues(vals);
      setIsAutoGenerated(result.data.isAutoGeneratedPurchaseOrder);
    }
  };

  useEffect(() => {
    if (id) {
      getProducts();
    }
  }, []);

  const handleAddProduct = async (item, ref) => {
    const data = { productId: item.id, orderId: id, originalQuantityDu: ref?.current?.valueAsNumber };
    const result = await PurchaseOrderProductsApi.create(data);
    if (result.status === HttpStatus.OK) {
      getProducts();
    }
  };

  const handleProductUpdate = (product, event) => {
    var newValue = event.target.valueAsNumber;
    var newOrderProducts = [];
    for (let index = 0; index < purchaseOrderProducts.length; index++) {
      const op = purchaseOrderProducts[index];
      if (op.id === product.id) {
        op.originalQuantityDu = newValue;
      }
      newOrderProducts.push(op);
    }
    setPurchaseOrderProducts(newOrderProducts);
  };

  const handleProductSave = async (product, e) => {
    const res = await PurchaseOrderProductsApi.update(product.id, { originalQuantityDu: e.target.valueAsNumber });
    if (res.status === StatusCodes.OK) {
      // Update this product in the list
      const newOrderProducts = purchaseOrderProducts.map((op) => {
        if (op.id === product.id) {
          op = res.data;
        }
        return op;
      });
      setPurchaseOrderProducts(newOrderProducts);
    }
  };

  const handleDeleteProduct = async (popId: string): Promise<void> => {
    await PurchaseOrderProductsApi.delete(popId);
    getProducts();
  };

  const ProductNameCell = ({ dataItem }: ProductCellProps): JSX.Element => {
    const ref = React.createRef();
    return (
      <td>
        <Form>
          <Form.Row>
            <StyleCol xs={5}>
              <ActionsWrapper style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <StyleInput type="number" className="form-control " ref={ref} />
                <WrapperPlusIcon>
                  <BorderlessButton onClick={() => handleAddProduct(dataItem, ref)} iconSize={25} icon={plus} />
                </WrapperPlusIcon>
              </ActionsWrapper>
            </StyleCol>
            <StyleCol xs={7}>
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                {dataItem.duPerPallet && <PalletDu>{dataItem.duPerPallet}</PalletDu>}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <ProductName>{dataItem.name}</ProductName>
                  <ProductName>{dataItem.manufacturerName}</ProductName>
                </div>
              </div>
            </StyleCol>
          </Form.Row>
        </Form>
      </td>
    );
  };

  const barcodeCell = ({ dataItem }) => {
    return (
      <td>
        <div>
          <GtinComponent text="CU" code={dataItem.gtinCu} background="#F4F4F4" />
          <br />
          <GtinComponent text="DU" code={dataItem.gtinDu} background="#F4F4F4" />
        </div>
      </td>
    );
  };

  const onSubmitMine = async (values, setSubmitting) => {
    setSubmitting(true);
    const files = values.xlsx;
    if (files && files.length > 0) {
      const fileData = { files, type: FILE_UPLOAD_TYPES.PRODUCT_SELECTION };
      const fileUploadResp = await FilesApi.create(fileData);
      if (fileUploadResp.status === StatusCodes.OK) {
        const result = await PurchaseOrdersApi.importOrderProducts(fileUploadResp.data[0].id, id);
        setResult(result.data);
        if (result.status === StatusCodes.OK) {
          setIsImport(!isImport);
        }
      }
    }

    getProducts();
    setSubmitting(false);
  };

  return (
    <WhitePad isFluid>
      <PurchaseSecondaryNavigation />
      <TitleComponent
        titleRight="Add"
        iconRight={importIcon}
        onRightHandler={() => {
          setIsImport(!isImport);
        }}
        title="Purchase Order Products"
      />
      {isAutoGenerated && (
        <p style={{ color: 'red', fontWeight: 'bold', textAlign: 'center' }}>
          This order is auto-generated and product editing is disabled.
        </p>
      )}
      {isImport && (
        <ModalStyle isOpen={isImport}>
          <AlignModal>
            <TitleModalWrapper>
              <TitleComponent
                Heading="h3"
                title="Import Instructions"
              // titleRight="Add"
              // iconRight={xOutline}
              // onRightHandler={() => {
              //   setIsImport(!isImport);
              // }}
              />
            </TitleModalWrapper>
            <WrapperCloseBtn>
              <BorderlessButton
                icon={xCircle}
                iconSize={35}
                onClick={() => {
                  setIsImport(!isImport);
                }}
              />
            </WrapperCloseBtn>
            <BoxWrapper>
              <div style={{ paddingBottom: 47 }}>
                <ImportOrderProducts />
              </div>
              <Formik
                initialValues={{
                  xlsx: [],
                }}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting }) => onSubmitMine(values, setSubmitting)}
              >
                {({ values, handleChange, handleBlur, isSubmitting, setFieldValue, onSubmit, handleSubmit }) => (
                  <form autoComplete="off" onSubmit={handleSubmit} style={{ width: 480 }}>
                    <FormRow style={{ width: 480 }}>
                      <FileInputComponent
                        name="xlsx"
                        value={values.xlsx}
                        onBlur={handleBlur}
                        setFieldValue={setFieldValue}
                        cols={24}
                      />
                    </FormRow>
                    <SecondaryButton icon={upload} type="submit" isSubmitting={isSubmitting} title="Upload" />
                  </form>
                )}
              </Formik>
              <ImportResults result={result} />
            </BoxWrapper>
          </AlignModal>
        </ModalStyle>
      )}
      {!isImport && (
        <>
          <div className="row">
            {!isAutoGenerated && (
              <>
                <div className="col-6 table">
                  <GridWrapper>
                    <TableWrapper>
                      <StatefullGrid>
                        <GridColumn
                          field="name"
                          title="Product Name"
                          cell={(props): JSX.Element => ProductNameCell(props, handleAddProduct, 'Add')}
                        />
                        <GridColumn
                          field="gtin"
                          title="Barcode"
                          cell={barcodeCell}
                          sortable={false}
                        />
                        <GridColumn
                          field="availableQuantities"
                          title="Available Quantities"
                          filterable={false}
                          sortable={false}
                          cell={(props): JSX.Element => (
                            <td>
                              <DisplayQuantities
                                cuQuantity={props.dataItem.availableCu}
                                dpQuantity={props.dataItem.availableDp}
                                duQuantity={props.dataItem.availableDu}
                              />
                            </td>
                          )}
                        />
                      </StatefullGrid>
                    </TableWrapper>
                  </GridWrapper>
                </div>
              </>
            )}
            <div className={`col-6 ${isAutoGenerated ? 'mx-auto' : ''}`}>
              <div className="table-responsive">
                <GridWrapper>
                  <TableWrapper>
                    <thead>
                      <tr>
                        <th style={{ width: '96px' }}>Initial Dus</th>
                        <th>Actual Quantities</th>
                        <th>Total Cus</th>
                        <th>Name</th>
                        <th>Barcodes </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseOrderProducts?.map((purchaseOrderProduct) => (
                        <tr key={purchaseOrderProduct.id}>
                          <td>
                            <TextInputComponent
                              type="number"
                              cols={24}
                              height={25}
                              className="form-control"
                              onChange={(e) => handleProductUpdate(purchaseOrderProduct, e)}
                              onBlur={(e) => handleProductSave(purchaseOrderProduct, e)}
                              value={purchaseOrderProduct.originalQuantityDu || ''}
                              disabled={isAutoGenerated}
                            />
                          </td>
                          <td>
                            <DisplayQuantities
                              cuQuantity={purchaseOrderProduct.quantityCu}
                              dpQuantity={purchaseOrderProduct.quantityDp}
                              duQuantity={purchaseOrderProduct.quantityDu}
                            />
                          </td>
                          <td>{purchaseOrderProduct.totalCUs}</td>
                          <td>{purchaseOrderProduct.productName}</td>
                          <td>
                            <GtinComponent text="CU" code={purchaseOrderProduct.gtinCu} background="#F4F4F4" />
                            <br />
                            <GtinComponent text="DU" code={purchaseOrderProduct.gtinDu} background="#F4F4F4" />
                          </td>
                          <td>
                            <BorderlessButton
                              onClick={(): Promise<void> => handleDeleteProduct(purchaseOrderProduct.id)}
                              icon={xIcon}
                              iconSize={25}
                              disabled={isAutoGenerated}
                            />
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td>
                          <b>{purchaseOrderProducts.reduce((sum, product) => sum + (product.quantityDu ?? 0), 0)}</b>
                        </td>
                        <td>
                          <b>
                            {`${purchaseOrderProducts.reduce((sum, product) => sum + (product.quantityDu ?? 0), 0)}/
                          ${purchaseOrderProducts.reduce((sum, product) => sum + (product.quantityCu ?? 0), 0)}/
                          ${purchaseOrderProducts.reduce((sum, product) => sum + (product.quantityDp ?? 0), 0)}`}
                          </b>
                        </td>
                        <td>
                          <b>
                            {purchaseOrderProducts.reduce(
                              (sum, product) => sum + ProductService.getTotalCu(
                                product.packageProducts,
                                product.quantityDu ?? 0,
                                0,
                                0,
                              ),
                              0,
                            )}
                          </b>
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                    </tbody>
                  </TableWrapper>
                </GridWrapper>
              </div>
            </div>
          </div>
        </>
      )}
    </WhitePad>
  );
}

const StyleInput = styled.input`
    height: 25px !important;
    padding: 0 !important;
    padding-left: 10px !important;
    border-color: #eeeeee !important;
    border: 1px solid #eeeeee;
    border-radius: 200px;

    &:active {
        box-shadow: none !important;
        border: 1px solid #eeeeee;
        border-color: #eeeeee !important;
    }

    &:hover {
        box-shadow: none !important;
        border: 1px solid #eeeeee;
        border-color: #eeeeee !important;
    }

    &:focus {
        box-shadow: none !important;
        border: 1px solid #eeeeee;
        border-color: #eeeeee !important;
    }
`;

const WrapperCloseBtn = styled.div`
    position: absolute;
    top: 30px;
    right: 30px;
    z-index: 9999;
`;

const TitleModalWrapper = styled.div`
  width: 912px;
`;

const AlignModal = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const ModalStyle = styled(Modal)`
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  overflow-y: scroll;
`;

const BoxWrapper = styled.div`
  background-color: #f4f4f4;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex-direction: column;
  padding: 57px 156px;
  border-radius: 10px;
  max-width: 960px;
`;

const WrapperPlusIcon = styled.div`
  margin: 0 8px 0 3px;
`;

const StyleCol = styled(Col)`
  padding: 0 !important;
  display: flex;
`;

const ActionsWrapper = styled.div`
  .form-group {
    margin: 0;
    padding: 0;
    border: 0;
  }
`;

const ProductName = styled.p`
  color: #343434;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  margin-bottom: 0;
  text-align: left;
`;

const PalletDu = styled.p`
  color: #343434;
  border-radius: 3px;
  border: 2px solid #343434;
  font-size: 14px;
  font-weight: 500;
  padding: 2px 3px;
  margin-right: 10px;
  margin-bottom: 0;
  text-align: left;
`;
